import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

const STAT_BIG = [
  ["pts", "PTS"], ["ast", "AST"], ["reb", "RT"], ["eff", "EF"],
];

export default function PublicPlayerDetail() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [team, setTeam] = useState(null);
  const [perGame, setPerGame] = useState([]);
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    api.get(`/players/${id}`).then(async (r) => {
      setPlayer(r.data);
      const t = await api.get(`/teams/${r.data.team_id}`);
      setTeam(t.data);
      const games = (await api.get("/games")).data.filter(
        (g) => g.team_a_id === r.data.team_id || g.team_b_id === r.data.team_id
      );
      const rows = [];
      const all = { pts: 0, ast: 0, reb: 0, oreb: 0, dreb: 0, tov: 0, pf: 0, pts1m: 0, pts1a: 0, pts2m: 0, pts2a: 0, eff: 0 };
      for (const g of games) {
        const stats = (await api.get(`/games/${g.id}/stats`)).data;
        const s = stats.per_player[id];
        if (s) {
          rows.push({ game: g, stats: s });
          for (const k of Object.keys(all)) all[k] += s[k] || 0;
        }
      }
      const fga = all.pts1a + all.pts2a;
      const fgm = all.pts1m + all.pts2m;
      all.fg_pct = fga ? Math.round((fgm / fga) * 1000) / 10 : 0;
      all.games = rows.length;
      all.ppg = rows.length ? Math.round((all.pts / rows.length) * 10) / 10 : 0;
      all.apg = rows.length ? Math.round((all.ast / rows.length) * 10) / 10 : 0;
      all.rpg = rows.length ? Math.round((all.reb / rows.length) * 10) / 10 : 0;
      setTotals(all);
      setPerGame(rows);
    });
  }, [id]);

  if (!player) return <div className="max-w-7xl mx-auto p-12 text-ink-muted">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-6 md:py-8">
      <Link to="/players" className="inline-flex items-center gap-1 text-ink-muted hover:text-orange text-xs uppercase tracking-widest font-heading mb-4">
        <ChevronLeft size={14} /> All players
      </Link>

      <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-1">
          <div className="aspect-[3/4] max-w-xs mx-auto lg:max-w-none bg-navy-deep border border-navy-border rounded-sm overflow-hidden relative">
            {player.photo_url ? <img src={fileUrl(player.photo_url)} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full grid place-items-center diagonal-stripes">
                <span className="font-heading font-black text-7xl md:text-9xl text-orange/40">{player.full_name?.[0]}</span>
              </div>}
            <div className="absolute top-3 left-3 bg-orange text-white font-mono tabular text-3xl md:text-5xl font-black px-2 md:px-3 leading-none py-1.5 md:py-2">#{player.jersey_number}</div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="text-orange font-heading uppercase tracking-[0.3em] text-xs">Player</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white break-words">{player.full_name}</h1>
            {team && <Link to={`/teams/${team.id}`} className="text-orange font-heading uppercase tracking-widest text-xs md:text-sm hover:underline">{team.name} →</Link>}
          </div>

          {totals && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
              {STAT_BIG.map(([k, l]) => (
                <div key={k} className="bg-navy-deep border border-navy-border rounded-sm p-3 md:p-4">
                  <div className="text-[10px] font-heading uppercase tracking-widest text-ink-muted">{l}</div>
                  <div className="font-mono tabular text-2xl md:text-3xl text-orange font-black">{totals[k] || 0}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                    {k === "pts" ? `${totals.ppg}/g` : k === "ast" ? `${totals.apg}/g` : k === "reb" ? `${totals.rpg}/g` : "total"}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totals && (
            <div className="bg-navy-deep border border-navy-border rounded-sm p-4 md:p-5">
              <h3 className="font-heading uppercase tracking-widest text-orange text-xs md:text-sm mb-3">Shooting</h3>
              <div className="grid grid-cols-3 gap-3 md:gap-4 text-sm">
                <div><div className="text-ink-muted text-xs">1PT</div><div className="font-mono tabular">{totals.pts1m}/{totals.pts1a}</div></div>
                <div><div className="text-ink-muted text-xs">2PT</div><div className="font-mono tabular">{totals.pts2m}/{totals.pts2a}</div></div>
                <div><div className="text-ink-muted text-xs">FG%</div><div className="font-mono tabular text-orange">{totals.fg_pct}%</div></div>
              </div>
            </div>
          )}
        </div>
      </div>

      <h2 className="font-heading uppercase tracking-widest text-orange text-xs md:text-sm mt-10 md:mt-12 mb-3">Game log</h2>
      <div className="border border-navy-border rounded-sm overflow-x-auto">
        <table className="w-full text-xs md:text-sm min-w-[520px]">
          <thead>
            <tr className="bg-navy-surface text-ink-muted text-[10px] uppercase tracking-widest">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Game</th>
              <th className="text-center px-2 py-2">PTS</th>
              <th className="text-center px-2 py-2">AST</th>
              <th className="text-center px-2 py-2">RT</th>
              <th className="text-center px-2 py-2">FG%</th>
              <th className="text-center px-2 py-2">EF</th>
            </tr>
          </thead>
          <tbody>
            {perGame.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-ink-muted text-sm">No game log yet.</td></tr>
            ) : perGame.map(({ game: g, stats: s }) => (
              <tr key={g.id} className="border-t border-navy-border">
                <td className="px-3 py-2 font-mono text-ink-muted">{g.date}</td>
                <td className="px-3 py-2"><Link to={`/games/${g.id}`} className="hover:text-orange">{g.score_a}–{g.score_b}</Link></td>
                <td className="text-center px-2 py-2 font-mono tabular">{s.pts}</td>
                <td className="text-center px-2 py-2 font-mono tabular">{s.ast}</td>
                <td className="text-center px-2 py-2 font-mono tabular">{s.reb}</td>
                <td className="text-center px-2 py-2 font-mono tabular">{s.fg_pct}%</td>
                <td className="text-center px-2 py-2 font-mono tabular text-orange">{s.eff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
